import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { libraryAPI } from '../api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Folder,
  BookOpen,
  TrendingUp,
  Calendar,
  Loader2
} from 'lucide-react';

const TopicsDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await libraryAPI.getTopicsSummary();
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading topics dashboard:', error);
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (topicName) => {
    navigate(`/library?topic=${encodeURIComponent(topicName)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/library')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Library
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Topic Dashboard</h1>
          <p className="text-gray-600">
            {dashboardData?.total_articles || 0} articles across {dashboardData?.topics?.length || 0} topics
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardData?.total_articles || 0}
                  </p>
                  <p className="text-sm text-gray-500">Total Articles</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardData?.total_read || 0}
                  </p>
                  <p className="text-sm text-gray-500">Articles Read</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Folder className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardData?.topics?.length || 0}
                  </p>
                  <p className="text-sm text-gray-500">Topics</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Topic Cards */}
        {dashboardData?.topics?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboardData.topics.map((topic, index) => (
              <Card
                key={index}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleTopicClick(topic.topic_name)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Folder className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {topic.topic_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {topic.total_saved_count} article{topic.total_saved_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary">
                      {topic.total_saved_count}
                    </Badge>
                  </div>

                  <div className="flex gap-4 text-sm">
                    {topic.new_this_week_count > 0 && (
                      <div className="flex items-center gap-1 text-green-600">
                        <Calendar className="h-4 w-4" />
                        <span>{topic.new_this_week_count} new this week</span>
                      </div>
                    )}
                    {topic.new_this_month_count > 0 && topic.new_this_week_count === 0 && (
                      <div className="flex items-center gap-1 text-blue-600">
                        <Calendar className="h-4 w-4" />
                        <span>{topic.new_this_month_count} new this month</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Topics Yet
              </h3>
              <p className="text-gray-600 mb-4">
                Save articles to your library to see them organized by topic
              </p>
              <Button onClick={() => navigate('/home')}>
                Browse Articles
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TopicsDashboardPage;
